import React, { useState } from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import { renderUserChips } from '../SetupChips.jsx';
import SignupLinksDialog from './SignupLinksDialog.jsx';

const CompaniesPanel = ({
    tab,
    companyOptions,
    userOptionIds,
    userById,
    loadingCompanies,
    loadingUsers,
    userLoadError,
    saving,
    getCompanyDisplayLabel,
    getUserDisplayLabel,
    getWasteReceiverDisplayLabel,
    getCompanyMembershipPermission,
}) => {
    const [signupLinksOpen, setSignupLinksOpen] = useState(false);
    const {
        companyDraft,
        companyDraftMode,
        companyFormTitle,
        companyUserCountById,
        getDraftCompanyMembershipPermission,
        isCreatingCompany,
        selectedCompany,
        selectedCompanyId,
        selectedCompanyUserIds,
        setCompanyDraft,
        setSelectedCompanyId,
        startCompanyEdit,
        updateCompanyDraftMembershipPermission,
        updateCompanyDraftUserIds,
    } = tab;

    const renderCompanyMembershipPermissionLabel = (permission) => {
        const labels = [];
        if (permission.canViewAllCompanyData) {
            labels.push('Kan se al virksomhedsdata');
        }
        if (permission.canViewPhysicalMachineData) {
            labels.push('Kan se data fra fysiske maskiner');
        }
        if (permission.canViewWasteReceiverSortingList) {
            labels.push('Kan se sorteringsliste (kun læsning)');
        }

        if (labels.length === 0) {
            return <span className="empty-inline">Kun egen app-data</span>;
        }

        return labels.join(' · ');
    };

    const renderCompanyForm = () => {
        if (!isCreatingCompany && !selectedCompany) return null;

        return (
            <div className="setup-detail-panel">
                <div className="setup-detail-header">
                    <div>
                        <h2>{companyFormTitle}</h2>
                        <p>
                            {isCreatingCompany
                                ? 'Firestore-id genereres automatisk ved oprettelse.'
                                : selectedCompany.id}
                        </p>
                    </div>
                </div>

                <div className="setup-form">
                    {isCreatingCompany ? (
                        <TextField
                            label="Navn"
                            value={companyDraft.name}
                            onChange={(event) =>
                                setCompanyDraft((currentDraft) => ({
                                    ...currentDraft,
                                    name: event.target.value,
                                }))
                            }
                            required
                            size="small"
                            fullWidth
                            autoFocus
                        />
                    ) : (
                        <TextField
                            label="Virksomhed"
                            value={getCompanyDisplayLabel(selectedCompany.id)}
                            size="small"
                            fullWidth
                            InputProps={{ readOnly: true }}
                        />
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={companyDraft.active !== false}
                                onChange={(event) =>
                                    setCompanyDraft((currentDraft) => ({
                                        ...currentDraft,
                                        active: event.target.checked,
                                    }))
                                }
                            />
                        }
                        label={companyDraft.active !== false ? 'Aktiv' : 'Inaktiv'}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={companyDraft.testCompany === true}
                                onChange={(event) =>
                                    setCompanyDraft((currentDraft) => ({
                                        ...currentDraft,
                                        testCompany: event.target.checked,
                                    }))
                                }
                            />
                        }
                        label="Test company"
                    />

                    <Autocomplete
                        multiple
                        filterSelectedOptions
                        loading={loadingUsers}
                        disabled={saving || loadingUsers || Boolean(userLoadError)}
                        classes={{
                            paper: 'setup-autocomplete-paper',
                            listbox: 'setup-autocomplete-listbox',
                            option: 'setup-autocomplete-option',
                        }}
                        options={userOptionIds}
                        value={companyDraft.userIds}
                        onChange={(_, value) => updateCompanyDraftUserIds(value)}
                        getOptionLabel={(option) => getUserDisplayLabel(option)}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;
                            const user = userById.get(option);

                            return (
                                <li key={key} {...optionProps}>
                                    <div className="user-option">
                                        <span>{user?.label || option}</span>
                                        <span>{user?.secondaryLabel || option}</span>
                                    </div>
                                </li>
                            );
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({ index });

                                return (
                                    <Chip
                                        key={key}
                                        {...tagProps}
                                        size="small"
                                        label={getUserDisplayLabel(option)}
                                        className="setup-chip"
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Brugere"
                                helperText="Tilføj eller fjern virksomhedsbrugere."
                                size="small"
                            />
                        )}
                    />

                    {userLoadError && <Alert severity="warning">{userLoadError}</Alert>}

                    {companyDraft.userIds.length > 0 && (
                        <div className="setup-form-section">
                            <div className="setup-form-section-header">
                                <h3>Rettigheder pr. bruger</h3>
                            </div>

                            <div className="company-permission-list">
                                {companyDraft.userIds.map((userId) => {
                                    const permission = getDraftCompanyMembershipPermission(userId);
                                    return (
                                        <div className="company-permission-row" key={`company-permission-${userId}`}>
                                            <TextField
                                                value={getUserDisplayLabel(userId)}
                                                size="small"
                                                fullWidth
                                                InputProps={{ readOnly: true }}
                                            />

                                            <div className="company-permission-controls">
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={permission.canViewAllCompanyData}
                                                            onChange={(event) =>
                                                                updateCompanyDraftMembershipPermission(
                                                                    userId,
                                                                    'canViewAllCompanyData',
                                                                    event.target.checked,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Kan se al virksomhedsdata"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={permission.canViewPhysicalMachineData}
                                                            onChange={(event) =>
                                                                updateCompanyDraftMembershipPermission(
                                                                    userId,
                                                                    'canViewPhysicalMachineData',
                                                                    event.target.checked,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Kan se data fra fysiske maskiner"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={permission.canViewWasteReceiverSortingList}
                                                            onChange={(event) =>
                                                                updateCompanyDraftMembershipPermission(
                                                                    userId,
                                                                    'canViewWasteReceiverSortingList',
                                                                    event.target.checked,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Kan se sorteringsliste (kun læsning)"
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderSelectedCompany = () => {
        if (!selectedCompany) {
            return (
                <div className="setup-detail-panel empty-state">
                    <h2>Ingen virksomhed valgt</h2>
                    <p>Vælg en virksomhed i listen for at redigere brugertilknytninger.</p>
                </div>
            );
        }

        return (
            <div className="setup-detail-panel">
                <div className="setup-detail-header">
                    <div>
                        <h2>{selectedCompany.label || selectedCompany.id}</h2>
                        <p>{selectedCompany.id}</p>
                    </div>
                    <div className="setup-detail-actions">
                        <Button
                            variant="outlined"
                            startIcon={<PersonAddIcon />}
                            onClick={() => setSignupLinksOpen(true)}
                        >
                            Tilmeldingslinks
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={startCompanyEdit}
                            disabled={saving || loadingUsers}
                        >
                            Rediger
                        </Button>
                    </div>
                </div>

                <div className="setup-field-grid">
                    <div className="setup-field-label">Navn</div>
                    <div>{selectedCompany.label || <span className="empty-inline">Ikke angivet</span>}</div>

                    <div className="setup-field-label">Affaldsmodtager</div>
                    <div>
                        {selectedCompany.wasteReceiver
                            ? getWasteReceiverDisplayLabel(selectedCompany.wasteReceiver)
                            : <span className="empty-inline">Ingen tilknytning</span>}
                    </div>

                    <div className="setup-field-label">Aktiv</div>
                    <div>{selectedCompany.active === false ? 'Nej' : 'Ja'}</div>

                    <div className="setup-field-label">Test company</div>
                    <div>{selectedCompany.testCompany ? 'Ja' : 'Nej'}</div>

                    <div className="setup-field-label">Brugere</div>
                    <div>{renderUserChips(selectedCompanyUserIds, getUserDisplayLabel)}</div>

                    <div className="setup-field-label">Rettigheder</div>
                    <div className="company-permission-summary-list">
                        {selectedCompanyUserIds.length === 0
                            ? <span className="empty-inline">Ingen brugere</span>
                            : selectedCompanyUserIds.map((userId) => {
                                const permission = getCompanyMembershipPermission(userId, selectedCompany.id);

                                return (
                                    <div key={`company-permission-summary-${userId}`} className="company-permission-summary-row">
                                        <strong>{getUserDisplayLabel(userId)}</strong>
                                        <span>{renderCompanyMembershipPermissionLabel(permission)}</span>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="setup-layout">
            <div className="setup-list-panel">
                <div className="setup-list-header">
                    <h2>Virksomheder</h2>
                </div>

                <div className="receiver-list">
                    {companyOptions.length === 0 && !loadingCompanies ? (
                        <Alert severity="info">Der er endnu ingen virksomheder.</Alert>
                    ) : null}

                    {companyOptions.map((company) => (
                        <button
                            key={company.id}
                            type="button"
                            className={`receiver-row ${
                                company.id === selectedCompanyId ? 'active' : ''
                            }`}
                            onClick={() => setSelectedCompanyId(company.id)}
                            disabled={companyDraftMode !== 'view'}
                        >
                            <span className="receiver-name">{company.label || company.id}</span>
                            <span className="receiver-meta">
                                {company.id} · {getWasteReceiverDisplayLabel(company.wasteReceiver)} ·{' '}
                                {companyUserCountById.get(company.id) || 0} brugere
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {companyDraftMode === 'view' ? renderSelectedCompany() : renderCompanyForm()}

            <SignupLinksDialog
                open={signupLinksOpen}
                company={selectedCompany}
                onClose={() => setSignupLinksOpen(false)}
            />
        </div>
    );
};

export default CompaniesPanel;
